import { useMutation, useQuery } from '@apollo/client';
import upperFirst from 'lodash/upperFirst';
import React, { useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { FeedbackModal, Tag } from '@eluve/blocks';
import { Stack, Text, useToast } from '@eluve/components';
import { useIsUserAppointmentOwner } from '@eluve/frontend-appointment-hooks';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const getFeedbackTagsQuery = graphql(`
  query getFeedbackTags {
    feedbackTags {
      __typename
      id
      name
      type
    }
  }
`);

const insertSummaryFeedbackMutation = graphql(`
  mutation insertSummaryFeedback(
    $appointmentId: uuid!
    $comment: String
    $summarySection: String
    $type: FeedbackTypeEnum!
    $llmOutputId: uuid
  ) {
    insertSummaryFeedbackOne(
      object: {
        appointmentId: $appointmentId
        comment: $comment
        summarySection: $summarySection
        type: $type
        llmOutputId: $llmOutputId
      }
    ) {
      id
      type
      __typename
    }
  }
`);

const updateSummaryFeedbackMutation = graphql(`
  mutation updateSummaryFeedback(
    $id: uuid!
    $comment: String
    $summarySection: String
    $rating: Int
  ) {
    updateSummaryFeedback(
      where: { id: { _eq: $id } }
      _set: {
        comment: $comment
        summarySection: $summarySection
        rating: $rating
      }
    ) {
      affectedRows
    }
  }
`);

const insertSummaryFeedbackTagsMutation = graphql(`
  mutation insertSummaryFeedbackTags(
    $feedbackTags: [SummaryFeedbackTagsInsertInput!]!
  ) {
    insertSummaryFeedbackTags(objects: $feedbackTags) {
      affectedRows
    }
  }
`);

export interface SummaryFeedbackModalProps {
  appointmentId: string;
  llmOutputId: string | null;
  summarySection?: string;
}

export type SummaryFeedbackTags = Tag & { type: FeedbackTypeEnum | null };

export const SummaryFeedbackModal: React.FC<SummaryFeedbackModalProps> = ({
  appointmentId,
  summarySection,
  llmOutputId,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();
  const isAppointmentOwner = useIsUserAppointmentOwner();

  const [feedbackScoreId, setFeedbackScoreId] = useState<string | null>(null);
  const [tags, setTags] = useState<SummaryFeedbackTags[]>([]);
  const [selectedFeedbackType, setFeedbackType] =
    useState<FeedbackTypeEnum | null>(null);

  const [feedbackId, setFeedbackId] = useState<string>();

  const { data: getFeedbackTagsData } = useQuery(getFeedbackTagsQuery);

  const [insertFeedbackMutation] = useMutation(insertSummaryFeedbackMutation, {
    onError: () => toast.error(t('appointment.feedback.summarySaveFailed')),
  });

  const [updateFeedbackMutation] = useMutation(updateSummaryFeedbackMutation, {
    onError: () => toast.error(t('appointment.feedback.summaryUpdateFailed')),
  });

  const [insertFeedbackTagMutation] = useMutation(
    insertSummaryFeedbackTagsMutation,
    {
      onError: () =>
        toast.error(t('appointment.feedback.summaryTagsUpdateFailed')),
    },
  );

  useEffect(() => {
    setTags(
      getFeedbackTagsData?.feedbackTags?.map((tag) => ({
        ...tag,
        isTagged: false,
      })) ?? [],
    );
  }, [getFeedbackTagsData]);

  const insertFeedback = async (
    type: FeedbackTypeEnum,
    comment?: string,
    section?: string,
  ) => {
    const { data } = await insertFeedbackMutation({
      variables: {
        appointmentId,
        type,
        comment: comment ?? null,
        summarySection: section ?? null,
        llmOutputId,
      },
    });

    const feedbackId = data?.insertSummaryFeedbackOne?.id;

    setFeedbackId(feedbackId);

    return feedbackId;
  };

  const onFeedbackClick = async (type: FeedbackTypeEnum) => {
    setFeedbackType(type);

    // save thumbs up / down feedback
    await insertFeedback(type);

    if (llmOutputId) {
      const result = await apiClient.llmOutputs.addUserFeedbackScore({
        params: { tenantId, llmOutputId },
        body: { value: type },
      });
      if (result.status === 201) {
        setFeedbackScoreId(result.body.scoreId);
      }
    }
  };

  const onTagPressedChange = async (tagId: number, isTagged: boolean) => {
    setTags(
      tags.map((t) => {
        if (t.id === tagId) {
          return { ...t, isTagged };
        }
        return t;
      }),
    );
  };

  const onAdditionalFeedbackSubmit = async (
    type: FeedbackTypeEnum | null,
    comment: string,
    tags: Tag[],
    rating: number | null,
  ) => {
    let summaryFeedbackId = feedbackId;

    if (summaryFeedbackId) {
      await updateFeedbackMutation({
        variables: {
          id: summaryFeedbackId,
          comment,
          summarySection: summarySection ?? null,
          rating,
        },
      });
    } else if (selectedFeedbackType) {
      summaryFeedbackId = await insertFeedback(
        selectedFeedbackType,
        comment,
        summarySection,
      );
    }

    if (llmOutputId && type) {
      if (feedbackScoreId) {
        await apiClient.llmOutputs.updateUserFeedbackScore({
          params: { tenantId, llmOutputId, scoreId: feedbackScoreId },
          body: { value: type, comment: comment ?? null },
        });
      } else {
        const result = await apiClient.llmOutputs.addUserFeedbackScore({
          params: { tenantId, llmOutputId },
          body: { value: type, comment: comment ?? null },
        });
        if (result.status === 201) {
          setFeedbackScoreId(result.body.scoreId);
        }
      }

      if (rating !== null) {
        await apiClient.llmOutputs.addUserRatingScore({
          params: { tenantId, llmOutputId },
          body: { value: rating, comment: comment ?? null },
        });
      }
    }

    const selectedTags = tags.filter((tag) => tag.isTagged);

    const feedbackTagsInput = selectedTags.map((tag) => ({
      summaryFeedbackId,
      feedbackTagId: tag.id,
    }));

    await insertFeedbackTagMutation({
      variables: {
        feedbackTags: feedbackTagsInput,
      },
    });

    if (llmOutputId) {
      const feedbackTagPromises = selectedTags.map((tag) =>
        apiClient.llmOutputs.addUserFeedbackTagScore({
          params: { tenantId, llmOutputId },
          body: { value: upperFirst(tag.name) },
        }),
      );

      await Promise.allSettled(feedbackTagPromises);
    }

    const message =
      type === 'POSITIVE'
        ? t('appointment.feedback.thanksPositive')
        : t('appointment.feedback.thanksNegative');
    toast.success(message);
  };

  const content = (
    <Stack justify="end" align="center" gap={2} direction="horizontal">
      <Text inline color="secondary" weight="regular">
        {t('appointment.feedback.wasNoteHelpful')}
      </Text>
      <FeedbackModal
        feedbackType={selectedFeedbackType}
        tags={[
          ...tags.filter((tag) => tag.type === selectedFeedbackType),
          ...tags.filter((tag) => tag.type !== selectedFeedbackType),
        ]}
        onFeedbackClick={onFeedbackClick}
        onTagPressedChange={onTagPressedChange}
        onAdditionalFeedbackSubmit={onAdditionalFeedbackSubmit}
      />
    </Stack>
  );

  if (!isAppointmentOwner) {
    return null;
  }

  return content;
};
